import React, { useRef, useState, useEffect } from 'react'
import DOMPurify from 'dompurify'
import QRCode from 'qrcode'
import { useTranslation } from 'react-i18next'
import {
  ChevronRightIcon,
  PrinterIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { isWeightBasedModule, getModuleCategory, fieldsFromCustomMetrics } from './workoutHelpers'
import { useFieldTemplates } from '../../hooks/useFieldTemplates'

// VH-114: the ONE workout sheet — the detailed reps/weight write-in table with
// handwriting cells, a "Date: __/__" line, and a single session QR printed at
// the bottom. (VH-113's separate "digital" card-grid layout and its
// per-exercise QRs were removed in VH-114.)
const SHEET_PRINT_CSS = `
  @page {
    size: A4 landscape;
    margin: 8mm;
  }
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  body {
    font-family: Arial, sans-serif;
    font-size: 11px;
    line-height: 1.3;
  }
  .page {
    width: 281mm;
    height: 194mm;
    padding: 4mm;
    position: relative;
    overflow: hidden;
    page-break-after: always;
    page-break-inside: avoid;
  }
  .page:last-child {
    page-break-after: auto;
  }
  @media screen {
    .page {
      background: white;
      margin-bottom: 10mm;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
  }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page {
      margin: 0;
      box-shadow: none;
    }
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #333;
    padding-bottom: 3mm;
    margin-bottom: 4mm;
  }
  .header h1 {
    font-size: 16px;
    font-weight: bold;
  }
  .header .date {
    font-size: 12px;
    color: #666;
  }
  .header .date-field {
    font-size: 11px;
    color: #666;
  }
  .header .date-field .date-underline {
    display: inline-block;
    width: 22mm;
    border-bottom: 1px solid #999;
    text-align: center;
    font-size: 12px;
    letter-spacing: 1px;
  }
  .header-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1mm;
  }
  .module-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 4mm;
  }
  th, td {
    border: 1px solid #ccc;
    padding: 2mm 3mm;
    text-align: left;
  }
  th {
    background-color: #f3f4f6;
    font-weight: bold;
    font-size: 10px;
    text-transform: uppercase;
  }
  .exercise-name {
    font-weight: bold;
  }
  .equipment {
    font-size: 9px;
    color: #666;
  }
  .scheme {
    text-align: center;
    font-weight: 600;
  }
  .pb {
    text-align: center;
    background-color: #fef3c7;
    font-weight: 600;
  }
  .input-cell {
    width: 45px;
    text-align: center;
    background-color: #fff;
  }
  .set-header {
    font-size: 9px;
    text-align: center;
    background-color: #e5e7eb;
  }
  .warmup-header {
    background-color: #fef3c7;
  }
  .target-row {
    background-color: #eff6ff;
  }
  .target-row td {
    font-size: 10px;
    color: #4b5563;
  }
  .input-row td {
    height: 28px;
  }
  .row-label {
    font-size: 8px;
    font-weight: bold;
    text-transform: uppercase;
    color: #6b7280;
    writing-mode: vertical-rl;
    text-orientation: mixed;
    padding: 2px;
  }
  .kg-reps-divider {
    border-right: 1px solid #6b7280 !important;
  }
  td div[style*="borderRight"] {
    border-right-color: #6b7280 !important;
  }
  .notes-section {
    margin-top: 4mm;
    border: 1px solid #ccc;
    padding: 3mm;
    min-height: 30mm;
  }
  .notes-title {
    font-weight: bold;
    margin-bottom: 2mm;
    font-size: 10px;
  }
  .notes-lines {
    margin-top: 2mm;
  }
  .notes-line {
    border-bottom: 1px solid #ddd;
    height: 6mm;
    width: 100%;
  }
  .footer {
    position: absolute;
    bottom: 4mm;
    left: 4mm;
    right: 4mm;
    text-align: center;
    font-size: 9px;
    color: #999;
    border-top: 1px solid #eee;
    padding-top: 2mm;
  }
  .content-area {
    height: calc(100% - 20mm);
    overflow: hidden;
  }
  /* VH-114 B1: QR (left) + Notes (right) sit in ONE unbreakable row at the end
     of each session's sheet, so the QR is never cut across a page break on
     multi-session prints. */
  .session-footer-row {
    display: flex;
    gap: 4mm;
    align-items: stretch;
    margin-top: 4mm;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .session-footer-row .notes-section {
    flex: 1;
    margin-top: 0;
  }
  .session-qr {
    flex: none;
    text-align: center;
    margin-top: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2mm;
    border: 1px solid #ccc;
  }
  .session-qr img {
    width: 28mm;
    height: 28mm;
    display: inline-block;
  }
  .session-qr .qr-caption {
    font-size: 9px;
    color: #666;
    margin-top: 1mm;
    max-width: 30mm;
  }
`

export function PrintViewModal({ program, onClose, onBack }) {
  const { t, i18n } = useTranslation()
  const { getTemplate, getFieldLabel } = useFieldTemplates()
  const printRef = useRef()
  const _isGerman = i18n.language === 'de'

  // VH: metric-based exercises (Tabata/HIIT/cardio/…) carry their planned
  // targets in `exercise.metric_values` (e.g. {duration: 30, rounds: 5}), NOT
  // in sets/reps. The print sheet used to ignore these and print blank write-in
  // lines. Resolve the exercise's field template (labels + units) the same way
  // the mobile ExerciseCard does, dropping the weight/reps fields (those belong
  // to the strength table). If no template matches, fall back to the keys
  // actually present in metric_values so we still print real values.
  //
  // VH-153: the exercise's OWN configured fields win over the per-type
  // template — the fifth surface to need this rule, found by sweeping rather
  // than by Reto reporting it a fifth time. This one matters more than it
  // looks: it is the sheet that gets PRINTED and carried to the gym, so a
  // missing Intensity or Steps column cannot be worked around on the spot.
  const getMetricFields = (exercise) => {
    const exType = exercise.snapshot_exercise_type || 'strength'
    const template = getTemplate(exType)
    const configured = fieldsFromCustomMetrics(exercise.snapshot_custom_metrics)
    const source = configured.length > 0 ? configured : (template?.fields || [])
    const fields = source.filter(
      (f) => f.key !== 'weight' && f.key !== 'reps'
    )
    if (fields.length > 0) return fields
    return Object.keys(exercise.metric_values || {}).map((key) => ({
      key,
      label_en: key,
      label_de: key
    }))
  }

  // VH: format one planned metric value ("30 min", "5"), or the blank write-in
  // line when that value is absent so the athlete can still fill it in by hand.
  const metricCellValue = (field, metricValues) => {
    const raw = metricValues?.[field.key]
    if (raw === undefined || raw === null || raw === '') return null
    return field.unit ? `${raw} ${field.unit}` : `${raw}`
  }

  // VH-114: pre-render ONE scannable QR (data URL) per SESSION, keyed on the
  // session's `intake_token`. A data-URL <img> survives the innerHTML copy into
  // the print window (a live <canvas> would not), so it prints reliably.
  // Rendered at high resolution (300px) so it stays crisp at print DPI (≥28mm).
  const [qrMap, setQrMap] = useState({})

  useEffect(() => {
    const tokens = []
    program?.sessions?.forEach((session) => {
      if (session.intake_token) tokens.push(session.intake_token)
    })
    if (tokens.length === 0) {
      setQrMap({})
      return
    }
    let cancelled = false
    Promise.all(
      tokens.map((tok) =>
        QRCode.toDataURL(`${window.location.origin}/capture/session/${tok}`, {
          width: 300,
          margin: 1,
        })
          .then((url) => [tok, url])
          .catch(() => null)
      )
    ).then((pairs) => {
      if (cancelled) return
      const map = {}
      pairs.forEach((pair) => {
        if (pair) map[pair[0]] = pair[1]
      })
      setQrMap(map)
    })
    return () => {
      cancelled = true
    }
  }, [program])

  // VH-114: the single session QR printed at the bottom of a session's sheet.
  const renderSessionQr = (session) => {
    const url = session.intake_token && qrMap[session.intake_token]
    if (!url) return null
    return (
      <div className="session-qr">
        <img src={url} alt={t('programs.print.scanToLog')} title={t('programs.print.scanToLog')} />
        <div className="qr-caption">{t('programs.print.scanToLog')}</div>
      </div>
    )
  }

  // VH-114: build + open the print window for the single workout sheet. The
  // preview in printRef already reflects the one-and-only layout.
  const doPrint = () => {
    const printContent = printRef.current
    if (!printContent) return
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('programs.week')} ${program.week_number}, ${program.year}</title>
        <style>${SHEET_PRINT_CSS}</style>
      </head>
      <body>
        ${DOMPurify.sanitize(printContent.innerHTML)}
      </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 250)
  }

  // ── The ONE workout sheet: detailed write-in reps/weight table + one session QR ──
  const renderSheet = () => (
    <>
      {program.sessions && program.sessions.map((session, sessionIdx) => (
        <div key={session.id} className="page">
          {/* Session Header */}
          <div className="header">
            <div className="flex items-center">
              {session.snapshot_module_color && (
                <span
                  className="module-color"
                  style={{ backgroundColor: session.snapshot_module_color, display: 'inline-block', width: '14px', height: '14px', borderRadius: '50%', marginRight: '8px' }}
                />
              )}
              <h1 className="text-xl font-bold">
                {session.snapshot_module_name_en}
                <span className="text-sm font-normal text-gray-400 ml-2">(S{session.id})</span>
              </h1>
            </div>
            <div className="header-right">
              <div className="date">
                {t('programs.week')} {program.week_number}, {program.year}
                {session.planned_day && ` • ${t(`programs.dayOfWeek.${session.planned_day}`)}`}
              </div>
              <div className="date-field">
                {t('programs.date', 'Date')}: <span className="date-underline">&nbsp;&nbsp;/&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
              </div>
            </div>
          </div>

          {/* Exercises Table - Adapted for module type */}
          {isWeightBasedModule(session.snapshot_module_type) ? (
            // Strength-based table with sets/reps/weight
            (() => {
              // Calculate max warmup and working sets across all exercises
              const maxWarmupSets = Math.max(
                ...session.exercises?.map(e => e.warmup_sets || 0) || [0],
                0
              )
              const maxWorkingSets = Math.max(
                ...session.exercises?.map(e => e.working_sets || 3) || [3],
                3
              )
              // VH-207: one legend line, printed only when an aim is on the sheet.
              const hasVariantTargets = (session.exercises || []).some(
                (e) => (e.set_results || []).some((sr) => sr.is_target && sr.actual_weight)
              )

              return (
                <>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-left" style={{ width: '20%' }}>
                        {t('trainingDays.exercise')}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-1 py-1 text-center" style={{ width: '3%' }}>
                        {/* Row label column - empty header */}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-center" style={{ width: '7%' }}>
                        {t('trainingDays.scheme')}
                      </th>
                      <th className="border border-gray-300 bg-yellow-100 px-2 py-1 text-center" style={{ width: '7%' }}>
                        PB
                      </th>
                      {/* Generate warmup set columns */}
                      {maxWarmupSets > 0 && [...Array(maxWarmupSets)].map((_, idx) => (
                        <th key={`warmup-${idx}`} className="border border-gray-300 bg-yellow-50 px-1 py-1 text-center text-xs">
                          <span className="text-yellow-700">W{idx + 1}</span>
                          <div className="flex text-xs mt-0.5">
                            <span className="flex-1 text-gray-500">kg</span>
                            <span className="flex-1 text-gray-500">reps</span>
                          </div>
                        </th>
                      ))}
                      {/* Generate working set columns */}
                      {[...Array(maxWorkingSets)].map((_, idx) => (
                        <th key={`working-${idx}`} className="border border-gray-300 bg-gray-100 px-1 py-1 text-center text-xs">
                          S{idx + 1}
                          <div className="flex text-xs mt-0.5">
                            <span className="flex-1 text-gray-500">kg</span>
                            <span className="flex-1 text-gray-500">reps</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {session.exercises && session.exercises
                      .slice()
                      .sort((a, b) => a.exercise_order - b.exercise_order)
                      .map((exercise) => {
                        // Check if this specific exercise is non-weight-based
                        const exType = exercise.snapshot_exercise_type || 'strength'
                        const isExWeightBased = isWeightBasedModule(exType)
                        const exerciseWarmupSets = exercise.warmup_sets || 0
                        const exerciseWorkingSets = exercise.working_sets || 3
                        const totalCols = maxWarmupSets + maxWorkingSets

                        if (!isExWeightBased) {
                          // Non-weight exercise (Tabata, HIIT, cardio, …) - print
                          // the PLANNED metric targets from metric_values instead
                          // of blank write-in lines. First field (duration) goes in
                          // the scheme/PB cell; the rest (rounds/distance/…) in the
                          // wide cell. Missing values still fall back to a blank line.
                          const metricFields = getMetricFields(exercise)
                          const metricValues = exercise.metric_values || {}
                          const firstField = metricFields[0]
                          const restFields = metricFields.slice(1)
                          const firstVal = firstField ? metricCellValue(firstField, metricValues) : null
                          return (
                            <tr key={exercise.id} className="bg-purple-50">
                              <td className="border border-gray-300 px-2 py-2">
                                <div className="font-semibold">{exercise.snapshot_exercise_name_en}</div>
                                <div className="text-xs text-purple-600 capitalize">{exType}</div>
                              </td>
                              <td className="border border-gray-300 px-1 py-2 text-center" style={{ fontSize: '7px', fontWeight: 'bold' }}>
                                {/* Empty for non-weight */}
                              </td>
                              <td className="border border-gray-300 px-2 py-2 text-center font-medium" colSpan="2">
                                {firstField
                                  ? `${getFieldLabel(firstField, i18n.language)}: ${firstVal ?? '________________'}`
                                  : `${t('programs.exercise.duration')}: ________________`}
                              </td>
                              <td className="border border-gray-300 px-2 py-2 text-center" colSpan={totalCols}>
                                {restFields.length > 0
                                  ? restFields.map((field, idx) => (
                                      <span key={field.key}>
                                        {idx > 0 && ' • '}
                                        {getFieldLabel(field, i18n.language)}: {metricCellValue(field, metricValues) ?? '________'}
                                      </span>
                                    ))
                                  : (exType === 'tabata'
                                      ? `${t('programs.exercise.rounds')}: ________`
                                      : `${t('common.notes')}: ________________`)}
                              </td>
                            </tr>
                          )
                        }

                        // Regular strength exercise - TWO rows per exercise
                        const pbWeight = exercise.personal_best?.max_weight
                        const pbReps = exercise.personal_best?.max_weight_reps
                        // Use program's stored set_results (updated by VH-26 refresh),
                        // fall back to best_session_sets (live historical query)
                        const targetSets = exercise.set_results?.length > 0
                          ? exercise.set_results
                          : (exercise.best_session_sets || [])
                        const warmupBestSets = targetSets.filter(s => s.set_type === 'warmup')
                        const workingBestSets = targetSets.filter(s => s.set_type === 'working')

                        return (
                          <React.Fragment key={exercise.id}>
                            {/* Row 1: Exercise info + Target values from program set_results */}
                            <tr style={{ backgroundColor: '#eff6ff' }}>
                              <td rowSpan="2" className="border border-gray-300 px-2 py-1 align-top">
                                <div className="font-semibold">{exercise.snapshot_exercise_name_en}</div>
                                <div className="text-xs text-gray-500">{exercise.snapshot_equipment_name_en}</div>
                              </td>
                              <td className="border border-gray-300 px-1 py-0 text-center" style={{ backgroundColor: '#dbeafe', fontSize: '7px', fontWeight: 'bold', color: '#1e40af' }}>
                                TARGET
                              </td>
                              <td rowSpan="2" className="border border-gray-300 px-2 py-1 text-center font-medium align-middle">
                                {exerciseWarmupSets > 0 && <span className="text-yellow-700 text-xs">{exerciseWarmupSets}W+</span>}
                                {exerciseWorkingSets}×{exercise.target_reps_min}
                                {exercise.target_reps_max && exercise.target_reps_max !== exercise.target_reps_min && `-${exercise.target_reps_max}`}
                              </td>
                              <td rowSpan="2" className="border border-gray-300 px-2 py-1 text-center font-medium align-middle" style={{ backgroundColor: '#fef3c7' }}>
                                {pbWeight ? `${pbWeight}×${pbReps}` : '-'}
                              </td>
                              {/* Warmup target cells */}
                              {maxWarmupSets > 0 && [...Array(maxWarmupSets)].map((_, idx) => {
                                const isWithinWarmup = idx < exerciseWarmupSets
                                const bestSet = warmupBestSets[idx]
                                return (
                                  <td key={`warmup-target-${idx}`} className="border border-gray-300 px-0 py-1 text-center text-xs" style={{ backgroundColor: isWithinWarmup ? '#fefce8' : '#f3f4f6' }}>
                                    {isWithinWarmup && (
                                      <div style={{ display: 'flex' }}>
                                        <div style={{ flex: 1, borderRight: '1px solid #d1d5db', paddingRight: '2px', fontWeight: 400, color: '#9ca3af', fontSize: '9px' }}>
                                          {bestSet?.actual_weight ? `${bestSet.actual_weight}` : '-'}
                                          {/* VH-207: paper cannot be tapped — an aim is marked in ink. */}
                                          {bestSet?.actual_weight && bestSet?.is_target ? (
                                            <span data-testid="print-target-mark" style={{ fontWeight: 700, color: '#1e40af' }}>*</span>
                                          ) : null}
                                        </div>
                                        <div style={{ flex: 1, paddingLeft: '2px', fontWeight: 400, color: '#9ca3af', fontSize: '9px' }}>
                                          {bestSet?.actual_reps || '-'}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                )
                              })}
                              {/* Working target cells */}
                              {[...Array(maxWorkingSets)].map((_, idx) => {
                                const isWithinWorking = idx < exerciseWorkingSets
                                const bestSet = workingBestSets[idx]
                                return (
                                  <td key={`working-target-${idx}`} className="border border-gray-300 px-0 py-1 text-center text-xs" style={{ backgroundColor: isWithinWorking ? '#f0f9ff' : '#f3f4f6' }}>
                                    {isWithinWorking && (
                                      <div style={{ display: 'flex' }}>
                                        <div style={{ flex: 1, borderRight: '1px solid #d1d5db', paddingRight: '2px', fontWeight: 400, color: '#9ca3af', fontSize: '9px' }}>
                                          {bestSet?.actual_weight ? `${bestSet.actual_weight}` : '-'}
                                          {/* VH-207: paper cannot be tapped — an aim is marked in ink. */}
                                          {bestSet?.actual_weight && bestSet?.is_target ? (
                                            <span data-testid="print-target-mark" style={{ fontWeight: 700, color: '#1e40af' }}>*</span>
                                          ) : null}
                                        </div>
                                        <div style={{ flex: 1, paddingLeft: '2px', fontWeight: 400, color: '#9ca3af', fontSize: '9px' }}>
                                          {bestSet?.actual_reps || '-'}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                )
                              })}
                            </tr>
                            {/* Row 2: Empty cells for handwriting actual results */}
                            <tr>
                              <td className="border border-gray-300 px-1 py-0 text-center" style={{ backgroundColor: '#fef3c7', fontSize: '8px', fontWeight: 'bold', color: '#92400e' }}>
                                ▶ ACTUAL
                              </td>
                              {/* Warmup input cells */}
                              {maxWarmupSets > 0 && [...Array(maxWarmupSets)].map((_, idx) => {
                                const isWithinWarmup = idx < exerciseWarmupSets
                                return (
                                  <td key={`warmup-input-${idx}`} className="border border-gray-300 px-0 text-center" style={{ height: '28px', backgroundColor: isWithinWarmup ? '#fefce8' : '#f3f4f6' }}>
                                    {isWithinWarmup && (
                                      <div style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                                        <div style={{ flex: 1, borderRight: '1px solid #9ca3af', fontSize: '8px', color: '#d1d5db' }}>[__]</div>
                                        <div style={{ flex: 1, fontSize: '8px', color: '#d1d5db' }}>[__]</div>
                                      </div>
                                    )}
                                  </td>
                                )
                              })}
                              {/* Working input cells */}
                              {[...Array(maxWorkingSets)].map((_, idx) => {
                                const isWithinWorking = idx < exerciseWorkingSets
                                return (
                                  <td key={`working-input-${idx}`} className="border border-gray-300 px-0 text-center" style={{ height: '28px', backgroundColor: !isWithinWorking ? '#f3f4f6' : '#ffffff' }}>
                                    {isWithinWorking && (
                                      <div style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                                        <div style={{ flex: 1, borderRight: '1px solid #9ca3af', fontSize: '8px', color: '#d1d5db' }}>[__]</div>
                                        <div style={{ flex: 1, fontSize: '8px', color: '#d1d5db' }}>[__]</div>
                                      </div>
                                    )}
                                  </td>
                                )}
                              )}
                            </tr>
                          </React.Fragment>
                        )
                      })}
                  </tbody>
                </table>
                {hasVariantTargets && (
                  <p data-testid="print-target-legend" style={{ fontSize: '9px', color: '#1e40af', marginTop: '1mm' }}>
                    {t('variants.printLegend')}
                  </p>
                )}
                </>
              )
            })()
          ) : (
            // Non-weight table for HIIT/Cardio/Group Class
            (() => {
              const sessionCategory = getModuleCategory(session.snapshot_module_type)
              return (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-left" style={{ width: '35%' }}>
                        {t('trainingDays.exercise')}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-center" style={{ width: '15%' }}>
                        {t('programs.exercise.target')}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-center" style={{ width: '15%' }}>
                        {t('programs.exercise.duration')}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-center" style={{ width: '15%' }}>
                        {sessionCategory === 'hiit' ? t('programs.exercise.rounds') :
                         sessionCategory === 'cardio' ? t('programs.exercise.distance') :
                         t('programs.exercise.intensity')}
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-left" style={{ width: '20%' }}>
                        {t('common.notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.exercises && session.exercises
                      .slice()
                      .sort((a, b) => a.exercise_order - b.exercise_order)
                      .map((exercise) => {
                        // Print the exercise's PLANNED metric targets from
                        // metric_values. Duration column ← the `duration` field;
                        // the last column (rounds/distance/intensity, per the
                        // header) ← the matching field for this module category.
                        // Missing values fall back to a blank write-in line.
                        const metricFields = getMetricFields(exercise)
                        const metricValues = exercise.metric_values || {}
                        const durationField = metricFields.find((f) => f.key === 'duration')
                        const secondaryKey = sessionCategory === 'hiit'
                          ? 'rounds'
                          : sessionCategory === 'cardio'
                            ? 'distance'
                            : 'intensity'
                        const secondaryField =
                          metricFields.find((f) => f.key === secondaryKey) ||
                          metricFields.find((f) => f.key !== 'duration')
                        const durationVal = durationField ? metricCellValue(durationField, metricValues) : null
                        const secondaryVal = secondaryField ? metricCellValue(secondaryField, metricValues) : null
                        return (
                          <tr key={exercise.id}>
                            <td className="border border-gray-300 px-2 py-2">
                              <div className="font-semibold">{exercise.snapshot_exercise_name_en}</div>
                              <div className="text-xs text-gray-500">{exercise.exercise_notes}</div>
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-center font-medium">
                              {session.snapshot_estimated_duration ? `${session.snapshot_estimated_duration} min` : '-'}
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-center">
                              {durationVal ?? '________________'}
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-center">
                              {secondaryVal ?? '________________'}
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                              ________________
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              )
            })()
          )}

          {/* VH-114 B1: QR (left) + Notes (right) in one unbreakable row so the
              QR is never cut across a page break on multi-session prints. */}
          <div className="session-footer-row">
            {renderSessionQr(session)}
            <div className="notes-section">
              <div className="notes-title">{t('common.notes')}:</div>
              {session.session_notes && (
                <div className="text-sm text-gray-600 mb-2">{session.session_notes}</div>
              )}
              <div className="notes-lines">
                <div className="notes-line"></div>
                <div className="notes-line"></div>
                <div className="notes-line"></div>
                <div className="notes-line"></div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-400 mt-4 pt-2 border-t border-gray-200">
            VitaHub Workouts • {t('programs.week')} {program.week_number}/{program.year} • {t('programs.sessions')} {sessionIdx + 1}/{program.sessions?.length}
          </div>
        </div>
      ))}
    </>
  )

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-surface-container z-10">
          <div className="flex items-center">
            <button onClick={onBack} className="mr-3 text-gray-400 hover:text-gray-600">
              <ChevronRightIcon className="h-5 w-5 rotate-180" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('programs.print.printView')} - {t('programs.week')} {program.week_number}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={doPrint} className="btn-primary flex items-center">
              <PrinterIcon className="h-4 w-4 mr-2" />
              {t('programs.print.title')}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 bg-gray-100">
          {/* Print Preview — the single workout sheet with the session QR. */}
          <div ref={printRef} className="bg-surface-container">
            {renderSheet()}
          </div>
        </div>
      </div>
    </div>
  )
}
